import React, {useCallback} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import EntryDAO from 'dao/EntryDAO';
import BaseCreateModal from 'folder-items/BaseCreateModal';
import {FolderDefaultsKey} from 'FolderDefaults';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import {prepareNotePartForSave} from 'notebook/notes/NoteStoreTransforms';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {StudyAssociation} from 'studies/StudyableDAO';

import {DisplayMessageAssistantResponseType} from './MessageTypes';
import {getMessageResponseTypeAsText} from './MessageUtils';

export interface SaveChatAssistantMessageAsEntryOrTemplateResult {
  targetType: string;
  targetId: string;
  url: string;
}

export interface SaveChatAssistantMessageAsEntryOrTemplateModalProps {
  targetType: string;
  initialNoteParts: Array<NotePart>;
  resolveNoteParts?: (folderId: string) => Promise<Array<NotePart>>;
  initialName: string;
  /** Drives the modal title segment after "Save" (reply → Message, report → Report). */
  messageResponseType: DisplayMessageAssistantResponseType;
  onRequestSubmit?: (result: SaveChatAssistantMessageAsEntryOrTemplateResult) => void | Promise<void>;
  onRequestClose: () => void;
  chatSessionId: string;
}

export function SaveChatAssistantMessageAsEntryOrTemplateModal({
  targetType,
  initialNoteParts,
  resolveNoteParts,
  initialName,
  messageResponseType,
  onRequestSubmit,
  onRequestClose,
  chatSessionId,
}: SaveChatAssistantMessageAsEntryOrTemplateModalProps): JSX.Element {
  const handleCreate = useCallback(
    async (
      name: string,
      targetApiId: string,
      tagSchemaId?: string,
      schemaTags?: TagUpdateObject,
      studyAssociations?: Array<StudyAssociation>
    ): Promise<string> => {
      const noteParts = resolveNoteParts != null ? await resolveNoteParts(targetApiId) : initialNoteParts;
      if (targetType === ModelTypeConstants.ENTRY) {
        // Create entry with initial note parts
        return Promise.resolve(
          EntryDAO.create({
            name: name.trim(),
            folder: targetApiId,
            tagSchemaId,
            schemaTags,
            studyAssociations,
            initialNoteParts: noteParts.map((notePart) => prepareNotePartForSave(notePart, {})),
            notePartsContentSourceId: chatSessionId,
          })
        ).then((result) => {
          if (!result) {
            throw new Error('Failed to create entry');
          }

          onRequestSubmit?.({
            targetType: ModelTypeConstants.ENTRY,
            targetId: result.id,
            url: `/eln/entry/${result.id}`,
          });

          return result.id;
        });
      } else {
        // Create template with selected collection
        // targetApiId is the template collection ID when creating templates
        return Promise.resolve(
          TemplateDAO.create({
            name: name.trim(),
            templateCollectionId: targetApiId,
            isProtected: false,
            tagSchema: tagSchemaId ? ({id: tagSchemaId} as TagSchema) : undefined,
            schemaTags,
            initialNoteParts: noteParts.map((notePart) => prepareNotePartForSave(notePart, {})),
            notePartsContentSourceId: chatSessionId,
          })
        ).then((result) => {
          if (!result) {
            throw new Error('Failed to create template');
          }

          onRequestSubmit?.({
            targetType: ModelTypeConstants.TEMPLATE,
            targetId: result.id,
            url: TemplateDAO.templateURL(result.id),
          });

          return result.id;
        });
      }
    },
    [targetType, initialNoteParts, resolveNoteParts, onRequestSubmit, chatSessionId]
  );

  const saveAsTitleSubject = getMessageResponseTypeAsText(messageResponseType);

  const modalTitle =
    targetType === ModelTypeConstants.ENTRY
      ? `Save ${saveAsTitleSubject} as Entry`
      : `Save ${saveAsTitleSubject} as Template`;

  return (
    <BaseCreateModal
      create={handleCreate}
      title={modalTitle}
      initialName={initialName}
      onRequestClose={onRequestClose}
      showInventory={false}
      showNotebook={targetType === ModelTypeConstants.ENTRY}
      modelType={targetType}
      folderDefaultsKey={FolderDefaultsKey.NOTEBOOK}
      entryProvenance="entry"
      hideStudiesInput={targetType === ModelTypeConstants.TEMPLATE}
    />
  );
}
